import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Row, Col, Table } from 'react-bootstrap';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  ReferenceLine
} from 'recharts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlay,
  faShieldHalved,
  faTriangleExclamation,
  faCircleCheck,
  faGaugeHigh
} from '@fortawesome/free-solid-svg-icons';
import { runSentinelAnalysis, SentinelAnalysisResult } from '../data-access/csa-plus.api';
import { setToast } from 'components/Toasts/data-access/toast-reducer';

const RISK_COLORS: Record<string, string> = {
  critical: '#dc3545',
  high: '#fd7e14',
  medium: '#ffc107',
  low: '#25b003'
};

const SEVERITY_COLORS: Record<string, string> = {
  critical: '#dc3545',
  high: '#fd7e14',
  medium: '#ffc107',
  low: '#25b003',
  informational: '#0dcaf0'
};

const CHART_COLORS = ['#305d84', '#3874ff', '#fd7e14', '#25b003', '#dc3545', '#6f42c1'];

const TrustGauge = ({ score }: { score: number }) => {
  const color = score >= 70 ? '#25b003' : score >= 40 ? '#ffc107' : '#dc3545';
  const label = score >= 70 ? 'Low Risk' : score >= 40 ? 'Medium Risk' : 'High Risk';
  return (
    <div className="text-center">
      <div
        style={{
          width: 120,
          height: 120,
          borderRadius: '50%',
          border: `8px solid ${color}`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 8px'
        }}
      >
        <span style={{ fontSize: 32, fontWeight: 700, color }}>{score}</span>
        <span style={{ fontSize: 11, color: '#6b7280' }}>/ 100</span>
      </div>
      <span className="badge" style={{ backgroundColor: color, fontSize: 12 }}>
        {label}
      </span>
    </div>
  );
};

const SummaryCard = ({
  icon,
  label,
  value,
  color
}: {
  icon: any;
  label: string;
  value: string | number;
  color: string;
}) => (
  <div className="rounded-3 border p-3 d-flex align-items-center gap-3" style={{ backgroundColor: '#fafafa' }}>
    <div
      className="d-flex align-items-center justify-content-center rounded-2 flex-shrink-0"
      style={{ width: 40, height: 40, backgroundColor: `${color}18` }}
    >
      <FontAwesomeIcon icon={icon} style={{ color, fontSize: 18 }} />
    </div>
    <div>
      <p className="mb-0 text-muted small">{label}</p>
      <p className="mb-0 fw-bold" style={{ fontSize: 20 }}>
        {value}
      </p>
    </div>
  </div>
);

export interface LogAnalysisTabProps {
  /** When true, only auto-run while this tab is selected (e.g. Cyber-AI → Log Analysis). */
  isTabActive?: boolean;
  /** Start Sentinel analysis once when the tab becomes active and there is no cached result. */
  autoRunWhenEmpty?: boolean;
  /** Hide the manual Process control (auto-run + retry-only UX). */
  hideProcessButton?: boolean;
}

const LogAnalysisTab = ({
  isTabActive = true,
  autoRunWhenEmpty = false,
  hideProcessButton = false
}: LogAnalysisTabProps = {}) => {
  const { projectId: projectIdParam } = useParams<{ projectId: string }>();
  const projectId = projectIdParam || '';
  const dispatch = useDispatch();
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState<SentinelAnalysisResult | null>(null);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const lastAutoRunSessionRef = useRef(false);

  const runAnalysis = useCallback(async () => {
    if (!projectId) return;
    setIsProcessing(true);
    setAnalysisError(null);
    try {
      const data = await runSentinelAnalysis({ projectId, save_result: true });
      setResult(data);
      dispatch(
        setToast({
          show: true,
          title: 'Analysis complete',
          message: `Processed ${data.summary.total_events} events. Trust score: ${data.summary.trust_score}.`
        })
      );
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Sentinel analysis failed.';
      setAnalysisError(msg);
      dispatch(
        setToast({
          show: true,
          title: 'Analysis failed',
          message: msg
        })
      );
    } finally {
      setIsProcessing(false);
    }
  }, [projectId, dispatch]);

  useEffect(() => {
    if (!isTabActive) {
      if (!isProcessing && analysisError == null) {
        lastAutoRunSessionRef.current = false;
      }
      return;
    }
    if (!autoRunWhenEmpty || !projectId || result != null) return;
    if (lastAutoRunSessionRef.current) return;
    lastAutoRunSessionRef.current = true;
    void runAnalysis();
  }, [isTabActive, autoRunWhenEmpty, projectId, result, runAnalysis, isProcessing, analysisError]);

  const plotData = result?.plot_data;
  const summary = result?.summary;
  const labels = plotData?.labels ?? {};

  const riskDistData = plotData
    ? ['critical', 'high', 'medium', 'low'].map(k => ({
        name: k.charAt(0).toUpperCase() + k.slice(1),
        value: plotData.risk_distribution[k] ?? 0,
        color: RISK_COLORS[k]
      }))
    : [];

  const vendorSevData = plotData
    ? Object.entries(plotData.vendor_severity_distribution).map(([k, v]) => ({
        name: k.charAt(0).toUpperCase() + k.slice(1),
        count: v,
        fill: SEVERITY_COLORS[k.toLowerCase()] ?? '#305d84'
      }))
    : [];

  const mitreData = (plotData?.mitre_distribution ?? []).slice(0, 5).map(([tech, count]) => ({
    name: labels.mitre_names?.[tech] ? `${tech}\n${labels.mitre_names[tech]}` : tech,
    count
  }));

  const cisData = (plotData?.cis_distribution ?? []).slice(0, 5).map(([cid, count]) => ({
    name: `CIS ${cid}${
      labels.cis_control_titles?.[String(cid)] ? `\n${labels.cis_control_titles[String(cid)].slice(0, 20)}` : ''
    }`,
    count
  }));

  const nistData = (plotData?.nist_distribution ?? []).slice(0, 5).map(([fn, count]) => ({
    name: `${fn}${labels.nist_full_names?.[fn] ? `\n${labels.nist_full_names[fn]}` : ''}`,
    count
  }));

  const endpointData = (plotData?.endpoint_risk ?? []).slice(0, 10).map(([ep, score]) => ({
    name: ep,
    score
  }));

  const actionItems = result?.action_required_high_critical ?? [];
  const totalEvents = summary?.total_events ?? 0;

  return (
    <div>
      {/* Header row */}
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div>
          <h6 className="fw-bold mb-1 text-body">Log Analysis</h6>
          <p className="text-muted small mb-0">
            Run composite risk analysis (MITRE ATT&amp;CK / CIS Controls / NIST CSF) on uploaded logs.
          </p>
        </div>
        {!hideProcessButton && (
          <button
            type="button"
            className="btn d-flex align-items-center gap-2 px-4 py-2"
            style={{ backgroundColor: '#305d84', color: '#fff', borderRadius: 8, fontWeight: 500 }}
            onClick={() => void runAnalysis()}
            disabled={isProcessing}
          >
            {isProcessing ? (
              <>
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                Processing...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faPlay} />
                Process
              </>
            )}
          </button>
        )}
      </div>

      {!result && !isProcessing && analysisError && (
        <div
          className="text-center py-4 rounded-3 border border-danger-subtle bg-danger-subtle"
          style={{ color: '#842029' }}
        >
          <p className="mb-3 small">{analysisError}</p>
          <button
            type="button"
            className="btn btn-sm btn-outline-danger"
            onClick={() => {
              lastAutoRunSessionRef.current = false;
              void runAnalysis();
            }}
          >
            Try again
          </button>
        </div>
      )}

      {!result && !isProcessing && !analysisError && (
        <div className="text-center py-5 rounded-3 border" style={{ backgroundColor: '#fafafa', color: '#9ca3af' }}>
          <FontAwesomeIcon icon={faShieldHalved} size="2x" className="mb-3 opacity-50" />
          <p className="mb-0 small">
            {hideProcessButton && autoRunWhenEmpty ? (
              <>
                {isTabActive ? (
                  <>Starting Sentinel analysis on your uploaded logs…</>
                ) : (
                  <>
                    Open the <strong>Log Analysis</strong> tab to run analysis automatically.
                  </>
                )}
              </>
            ) : (
              <>
                Click <strong>Process</strong> to run Sentinel analysis on your uploaded logs.
              </>
            )}
          </p>
        </div>
      )}

      {isProcessing && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary mb-3" role="status" />
          <p className="text-muted small">Running composite risk analysis…</p>
        </div>
      )}

      {result && !isProcessing && (
        <>
          {/* Summary row */}
          <Row className="g-3 mb-4">
            <Col xs={12} md={3}>
              <div
                className="rounded-3 border p-3 d-flex flex-column align-items-center justify-content-center h-100"
                style={{ backgroundColor: '#fafafa' }}
              >
                <p className="text-muted small mb-2">Trust Score</p>
                <TrustGauge score={summary!.trust_score} />
              </div>
            </Col>
            <Col xs={12} md={9}>
              <Row className="g-3 h-100">
                <Col xs={6} md={4}>
                  <SummaryCard icon={faGaugeHigh} label="Total Events" value={summary!.total_events} color="#305d84" />
                </Col>
                <Col xs={6} md={4}>
                  <SummaryCard
                    icon={faShieldHalved}
                    label="Total Findings"
                    value={summary!.total_findings}
                    color="#3874ff"
                  />
                </Col>
                <Col xs={6} md={4}>
                  <SummaryCard
                    icon={faTriangleExclamation}
                    label="Total Risk Score"
                    value={Math.round(summary!.total_risk_score)}
                    color="#fd7e14"
                  />
                </Col>
                {['critical', 'high', 'medium', 'low'].map(level => (
                  <Col xs={6} md={3} key={level}>
                    <SummaryCard
                      icon={faCircleCheck}
                      label={level.charAt(0).toUpperCase() + level.slice(1)}
                      value={summary!.risk_distribution[level] ?? 0}
                      color={RISK_COLORS[level]}
                    />
                  </Col>
                ))}
              </Row>
            </Col>
          </Row>

          {/* Charts row 1: Vendor Severity + Risk Distribution */}
          <Row className="g-4 mb-4">
            <Col xs={12} md={7}>
              <div className="rounded-3 border p-4 bg-white h-100">
                <h6 className="fw-semibold mb-3 text-body">Vendor Severity Distribution</h6>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={vendorSevData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Bar dataKey="count" name="Event Count" radius={[4, 4, 0, 0]}>
                      {vendorSevData.map((entry, i) => (
                        <Cell key={i} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Col>
            <Col xs={12} md={5}>
              <div className="rounded-3 border p-4 bg-white h-100">
                <h6 className="fw-semibold mb-3 text-body">Composite Risk Distribution</h6>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={riskDistData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {riskDistData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Col>
          </Row>

          {/* Charts row 2: MITRE + CIS */}
          <Row className="g-4 mb-4">
            <Col xs={12} md={6}>
              <div className="rounded-3 border p-4 bg-white h-100">
                <h6 className="fw-semibold mb-3 text-body">Top MITRE ATT&amp;CK Techniques</h6>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={mitreData} margin={{ top: 5, right: 10, left: 0, bottom: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-15} textAnchor="end" />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    {totalEvents > 0 && (
                      <ReferenceLine
                        y={totalEvents * 0.2}
                        stroke="#fd7e14"
                        strokeDasharray="4 4"
                        label={{ value: '20% threshold', position: 'right', fontSize: 10 }}
                      />
                    )}
                    <Bar dataKey="count" name="Technique Count" fill="#305d84" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Col>
            <Col xs={12} md={6}>
              <div className="rounded-3 border p-4 bg-white h-100">
                <h6 className="fw-semibold mb-3 text-body">Top CIS Controls Impacted</h6>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={cisData} margin={{ top: 5, right: 10, left: 0, bottom: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-15} textAnchor="end" />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    {totalEvents > 0 && (
                      <ReferenceLine
                        y={totalEvents * 0.1}
                        stroke="#fd7e14"
                        strokeDasharray="4 4"
                        label={{ value: '10% threshold', position: 'right', fontSize: 10 }}
                      />
                    )}
                    <Bar dataKey="count" name="Mapped Events" fill="#3874ff" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Col>
          </Row>

          {/* Charts row 3: NIST + Endpoint Risk */}
          <Row className="g-4 mb-4">
            <Col xs={12} md={5}>
              <div className="rounded-3 border p-4 bg-white h-100">
                <h6 className="fw-semibold mb-3 text-body">Top NIST CSF Functions</h6>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={nistData} margin={{ top: 5, right: 10, left: 0, bottom: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    {totalEvents > 0 && <ReferenceLine y={totalEvents * 0.1} stroke="#fd7e14" strokeDasharray="4 4" />}
                    <Bar dataKey="count" name="Mapped Events" radius={[4, 4, 0, 0]}>
                      {nistData.map((_, i) => (
                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Col>
            <Col xs={12} md={7}>
              <div className="rounded-3 border p-4 bg-white h-100">
                <h6 className="fw-semibold mb-3 text-body">Top Endpoints by Risk Score</h6>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={endpointData} layout="vertical" margin={{ top: 5, right: 20, left: 80, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={80} />
                    <Tooltip />
                    <Bar dataKey="score" name="Risk Score" fill="#dc3545" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Col>
          </Row>

          {/* Action required table */}
          {actionItems.length > 0 && (
            <div className="rounded-3 border p-4 bg-white mb-4">
              <h6 className="fw-semibold mb-3 text-body d-flex align-items-center gap-2">
                <FontAwesomeIcon icon={faTriangleExclamation} className="text-danger" />
                Action Required — Critical &amp; High Findings
                <span className="badge bg-danger ms-1">{actionItems.length}</span>
              </h6>
              <div style={{ overflowX: 'auto' }}>
                <Table size="sm" hover className="mb-0" style={{ fontSize: 12 }}>
                  <thead className="table-light">
                    <tr>
                      <th>Event ID</th>
                      <th>Endpoint</th>
                      <th>MITRE</th>
                      <th>CIS</th>
                      <th>Risk Level</th>
                      <th>Score</th>
                      <th>Recommended Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {actionItems.map((f, i) => (
                      <tr key={i}>
                        <td className="text-muted">{f.event_id}</td>
                        <td>{f.endpoint ?? '—'}</td>
                        <td>
                          {(f.mitre ?? []).map(t => (
                            <span key={t} className="badge bg-light text-dark border me-1">
                              {t}
                            </span>
                          ))}
                        </td>
                        <td>{(f.cis_controls ?? []).join(', ')}</td>
                        <td>
                          <span className="badge" style={{ backgroundColor: RISK_COLORS[f.risk_level] ?? '#6b7280' }}>
                            {f.risk_level}
                          </span>
                        </td>
                        <td className="fw-semibold">{Math.round(f.risk_score)}</td>
                        <td style={{ maxWidth: 260, whiteSpace: 'normal' }} className="text-muted">
                          {f.recommended_action ?? '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default LogAnalysisTab;
